// OnshapeSection — THE Onshape block (UI_VISION §8 step 1 / §9 "lego blocks").
// ONE component for every surface that shows an item's Onshape side: the
// Catalog family panel, the Product page's ONSHAPE section, and the Onshape
// screen's right panel. Variants are FLAGS, never copies (§4 "Flags, not
// forks" — before this, three files each hand-wrote their own card).
//
//   onshapeRef    — did/wid/eid; null/'' = not linked (the section still renders
//                   so a surface can offer its linking editor via `children`)
//   onshapeHref   — optional CONFIGURED url (carries `?configuration=…`) to use
//                   for "Open in Onshape" instead of the bare doc url (Bug 1,
//                   2026-07-21). SHARED component — product cards and the
//                   Onshape screen have no config and don't pass this, so the
//                   bare onshapeDocUrl stays the default for them, unchanged.
//   title         — section title (default '3D Model'). Alex, 2026-07-21:
//                   "Instead of Onshape, I would like to say 3D MODEL". The
//                   section describes WHAT IS THERE — a 3D model — not who
//                   hosts it, the same name-by-meaning rule icons.js follows.
//                   Vendor names are kept where they name a real DESTINATION
//                   ("Open in Onshape") or a vendor-specific identifier
//                   ("Onshape assembly (did/wid/eid)") — those would be wrong
//                   to genericise, because that is literally where you land and
//                   what you paste.
//   dbId          — 'prod:N' / 'fam:N' → DbIdBadge in the link row
//   linkTarget    — 'onshape' | 'db' | 'both' — what the link-click offers
//                   (Alex's example for the flag: in one place the click goes to
//                   Onshape, in another the same click goes to the DB). 'db'
//                   needs `dbHref` (deep link `/?view=…&node=…`).
//                   ⚠ 'db' suppresses BOTH Onshape affordances — the "Open in
//                   Onshape" anchor AND the OnshapeLink cube. The cube used to
//                   render unconditionally whenever `onshapeRef` was set, which
//                   was an oversight, not a decision: it is a link-click like
//                   any other, so `linkTarget` governs it. Found adopting panel
//                   mode (2026-07-21), where a link back to Onshape is exactly
//                   what Alex had removed ("Useless as we are in Onshape") — the
//                   shared card would have reinstated it. No effect on the other
//                   three consumers; none of them passes 'db'.
//   dbLinkNewTab  — open `dbHref` in a new tab. False by default: in-app
//                   surfaces navigate in place. True only for panel mode, which
//                   renders inside Onshape's IFRAME — a same-tab jump would load
//                   Core inside Onshape's frame instead of opening the app.
//   density       — 'normal' | 'shrink' | 'large'. 'large' only sets the STARTING size;
//                   the ⤢ control reaches the same height from 'normal', so a customer
//                   surface and an internal one differ by a default, not by a component
//                   (Alex 2026-08-04: "keep a lot of similarities between internal and
//                   customers… same root section that we have to improve").
//                   'normal' | 'shrink' — the narrow-panel variant (§9's
//                   "lego shrink block" is a flag on the same block).
//   showThumbnail — cache-first thumbnail with an explicit "Load thumbnail"
//                   button; the FIRST fetch (server cache miss) is always
//                   user-initiated. No ghost Onshape calls, ever.
//   showViewer    — "View 3D — ~3 calls" button; mounts the GLB viewer on click
//                   only (same quota discipline).
//   showRef       — print the raw mono ref line (the narrow panel wants it).
//   frame         — card border/background; false when the host provides chrome.
//   children      — surface-specific body: the family link editor, the product
//                   sync panel, the BOM panel.
//
// ── THE TWO AXES (Alex, 2026-07-21; UI_VISION §4) ────────────────────────────
// Alex asked for the section to exist EVERYWHERE with "a few states". Those
// states are not one list — they are TWO INDEPENDENT AXES, and keeping them
// apart is the whole design:
//
//   AVAILABILITY — a shared FACT about the data. Lives in the DB.
//   OPEN/CLOSED  — one user's preference. Lives in localStorage.
//
// Greyed is NOT a third open/closed value; it is the other axis, and it
// disables the toggle. Crossed, they give exactly the states Alex described:
//
//   applicable=false          → greyed title + `#NA`, no toggle, no body
//   applicable, not linked    → open shows the ADD-LINK editor (`children`)
//   applicable, linked        → open shows the card
//
//   applicable  — false = Onshape can never apply here. Today's only source is
//                 cost_class: labour (HR) has no CAD model, which is
//                 architecture, not preference ("Labour can never be modelled
//                 in CAD"). Default TRUE, so no caller regresses.
//   naReason    — tooltip on the `#NA` marker saying WHY.
//   collapsible — the title becomes the collapse control. Requires a `title`:
//                 the two surfaces that pass title='' (panel mode, the Onshape
//                 screen's right panel) have their own identity block above and
//                 nothing to hang a control on, so they stay uncollapsible.
//   storageKey  — per-SURFACE localStorage key. Deliberately not per-item:
//                 collapsing on one family should not re-open on the next.
//
// ⚠ DEFAULT IS **OPEN**, not collapsed. §4 says "collapsed by default where it
// is not immediately needed", but all four surfaces render expanded today, and
// Alex framed collapsing as the user's own choice ("just to save real estate by
// the user") — not as a default that hides things. Defaulting closed would be a
// regression on four surfaces at once, panel mode worst of all (it IS the
// register/link surface). Collapse still gates the fetches: a closed section
// renders no body, so the thumbnail/3D buttons cannot be reached and the quota
// rule holds either way.
//
// Quota rule inherited by construction: nothing in this file calls the Onshape
// API without a click that names its cost.

import { useState, useEffect } from 'react';
import Model3D from './Model3D.jsx';
import SectionTitle from './SectionTitle.jsx';
import Text from './Text.jsx';
import ImageCell from './ImageCell.jsx';
import { OnshapeLink, onshapeDocUrl } from '../OnshapeLink.jsx';
import DbIdBadge from './DbIdBadge.jsx';
import { LINK, WARN, VIEW_3D } from '../icons.js';
import { NAVY, CARD_STYLE, BTN_SECONDARY, BTN_DISABLED } from '../theme-tokens.js';

// GLB viewer — moved verbatim from ProductsView.jsx (2026-07-19) so the 3D
// block lives with the section that mounts it.

// Widest the viewport is allowed to get, as a multiple of its height. width:100% on a
// wide panel produced a letterbox slot (Alex, 2026-08-04: "the 3D window should have a
// ratio limit, too wide") — a shape that suits nothing, since a model framed to fit
// leaves most of it empty air. 1.6 is a touch wider than 3:2 and close to what Onshape's
// own viewport tends to be.
const MAX_ASPECT = 1.6;
export function OnshapeModelViewer({ onshapeRef, height = 420, onToggleLarge = null, large = false }) {
  const [status, setStatus] = useState('loading');
  const src = `/api/onshape/glb?ref=${encodeURIComponent(onshapeRef)}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <Text>{status}</Text>
        {/* Alex, 2026-08-04: the customer view wants "a bigger 3D view… same root section
            that we have to improve and then in the internal view, we can also extend and
            see the bigger 3D model". One control, both readings: internal expands on
            demand, and a customer surface can simply mount it already expanded. */}
        {onToggleLarge && (
          <button
            onClick={onToggleLarge}
            title={large ? 'Back to the inline size' : 'Enlarge the model'}
            style={{
              fontSize: 'var(--fs-small)', padding: '1px 7px', borderRadius: 3,
              border: '1px solid var(--th-border-subtle)', background: 'var(--th-row-even)',
              color: 'var(--th-text-secondary)', cursor: 'pointer', flexShrink: 0,
            }}
          >{large ? '⤡ Smaller' : '⤢ Bigger'}</button>
        )}
      </div>
      {/* THE 3D view (Model3D): Onshape gestures, Perspective toggle, opening view along
          the longest horizontal axis. Zoom has no floor — the old model-viewer limit
          ("Why Zoom in is limited?", Alex 2026-08-04) does not exist in it. */}
      <Model3D src={src} height={height} maxWidth={Math.round(height * MAX_ASPECT)} onStatus={setStatus} />
    </div>
  );
}

export default function OnshapeSection({
  onshapeRef,
  onshapeHref = null,
  title = '3D Model',
  dbId = null,
  linkTarget = 'onshape',
  dbHref = null,
  dbLinkNewTab = false,
  density = 'normal',
  showThumbnail = true,
  showViewer = true,
  showRef = false,
  frame = true,
  applicable = true,
  naReason = null,
  collapsible = true,
  // downloadName — what to call a file this section produces (today: the IFC). The SURFACE
  // knows the item's code and label; the server must not look them up, because the IFC path
  // is deliberately database-free so it can serve someone who has Onshape and no pricing
  // (check:ifc-standalone). So the name travels as a hint on the request, and the server
  // falls back to the cached Onshape element name, then the element id.
  downloadName = null,
  storageKey = 'default',
  children = null,
}) {
  const linked = !!onshapeRef;
  const shrink = density === 'shrink';
  const thumbSize = shrink ? 84 : 120;
  // 'large' is a third value on the SAME axis as 'shrink' — not a second component and
  // not a fork (UI_VISION §4 "flags, not forks"). A customer surface mounts at 'large';
  // internal mounts normal and the ⤢ control reaches the same size, which is exactly the
  // "keep a lot of similarities between internal and customers" Alex asked for.
  const [enlarged, setEnlarged] = useState(density === 'large');
  const viewerHeight = enlarged ? 620 : (shrink ? 260 : 420);

  // Open/closed — per SURFACE, per user. Read once on mount; a missing or
  // unreadable value (private mode) just means open, which is today's behaviour.
  const canCollapse = collapsible && !!title && applicable;
  const lsKey = `inoviu:section:onshape:${storageKey}`;
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(lsKey) === '1'; } catch { return false; }
  });
  const toggle = () => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(lsKey, next ? '1' : '0'); } catch { /* private mode — just won't persist */ }
      return next;
    });
  };

  // Thumbnail — cache-first; the button fetch warms the server cache, then the
  // <img> reads it. All state resets when the ref changes surface-side.
  const [thumbLoaded, setThumbLoaded] = useState(false);
  const [thumbLoading, setThumbLoading] = useState(false);
  const [thumbError, setThumbError] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  // null = not asked yet; the button keeps quoting the full cost until we know better,
  // so an unanswered probe can never under-state what a click will spend.
  const [cached, setCached] = useState(null);

  useEffect(() => {
    setThumbLoaded(false);
    setThumbLoading(false);
    setThumbError(null);
    setViewerOpen(false);
    setCached(null);
  }, [onshapeRef]);

  // A CACHED MODEL SHOWS ITSELF (Alex, 2026-08-04: "Does it really need an upload again
  // button?"). No — the button gates QUOTA, not rendering, and a cached model costs zero
  // Onshape calls. Making you click for something free is friction that also hid whether
  // the model was there at all. So: cached -> it just appears; not cached -> the button
  // stays, still naming its price. The quota rule is untouched, because it was never a
  // rule about clicking.
  //
  // What this does cost is local: a disk read and a Draco decode. `loading="lazy"` below
  // keeps that honest when many mount at once — "Expand all" on a placement grid would
  // otherwise start every viewer on the page at the same moment.
  useEffect(() => {
    if (cached?.glb) setViewerOpen(true);
    // Same reasoning one step earlier (Alex, 2026-08-05: "can it be turned on by default
    // when cache exists"). A cached thumbnail is a file already on disk; making you press
    // "Show thumbnail" for it was the button gating a free thing, exactly as the 3D button
    // did. Uncached still waits for a click, because that one costs a call.
    if (cached?.thumbnail) setThumbLoaded(true);
  }, [cached]);

  // Is the model already on disk? Filesystem existence only — no Onshape call, so this is
  // free to ask on every ref change. Without it the 3D button quotes "~3 calls" even when
  // the answer is sitting in the cache, and the only way to tell was to inspect the cache
  // directory by hand.
  useEffect(() => {
    if (!onshapeRef) return;
    let alive = true;
    fetch(`/api/onshape/cached?ref=${encodeURIComponent(onshapeRef)}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive && d) setCached(d); })
      .catch(() => { /* unknown stays unknown — the button keeps quoting the cost */ });
    return () => { alive = false; };
  }, [onshapeRef]);

  const loadThumb = () => {
    if (!onshapeRef || thumbLoading) return;
    setThumbLoading(true);
    setThumbError(null);
    fetch(`/api/onshape/thumbnail?ref=${encodeURIComponent(onshapeRef)}`)
      .then(async r => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error || `Thumbnail load failed (${r.status})`);
        }
        setThumbLoaded(true);
      })
      .catch(err => setThumbError(err.message))
      .finally(() => setThumbLoading(false));
  };

  const smallBtn = (disabled) => ({ ...BTN_SECONDARY, ...(disabled ? BTN_DISABLED : null) });

  const body = (
    <div style={{
      display: 'flex', gap: shrink ? 8 : 12, alignItems: 'flex-start', flexWrap: 'wrap',
      flexDirection: shrink ? 'column' : 'row',
    }}>
      {linked && showThumbnail && (
        <div style={{
          width: thumbSize, height: thumbSize, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'var(--th-row-even)', border: '1px solid var(--th-border-subtle)',
          borderRadius: 4, overflow: 'hidden',
        }}>
          {thumbLoaded ? (
            <img
              src={`/api/onshape/thumbnail?ref=${encodeURIComponent(onshapeRef)}`}
              alt="Onshape thumbnail"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          ) : (
            <button
              onClick={loadThumb}
              disabled={thumbLoading}
              style={{
                fontSize: 'var(--fs-small)', padding: '4px 8px', borderRadius: 3,
                border: '1px dashed var(--th-text-disabled)', background: 'var(--th-row-even)',
                color: thumbLoading ? 'var(--th-text-disabled)' : 'var(--th-text-secondary)',
                cursor: thumbLoading ? 'default' : 'pointer',
              }}
            >{thumbLoading ? 'Loading…' : (cached?.thumbnail ? 'Show thumbnail — cached' : 'Load thumbnail')}</button>
          )}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: shrink ? 0 : 200, alignSelf: 'stretch' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {linked && linkTarget !== 'db' && <OnshapeLink ref={onshapeRef} />}
          {linked && (linkTarget === 'onshape' || linkTarget === 'both') && (
            <a
              href={onshapeHref || onshapeDocUrl(onshapeRef) || undefined}
              target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 'var(--fs-small)', color: NAVY }}
            >Open in Onshape {LINK}</a>
          )}
          {(linkTarget === 'db' || linkTarget === 'both') && dbHref && (
            <a
              href={dbHref}
              {...(dbLinkNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              style={{ fontSize: 'var(--fs-small)', color: NAVY }}
            >Open in DB {LINK}</a>
          )}
          {dbId && <DbIdBadge dbId={dbId} />}
          {/* Only while there is still something to ask for. Once the viewer is up — by a
              click, or on its own because the model was cached — the button has nothing
              left to do, and leaving it behind as a dead "Loading 3D…" was worse than not
              having it. A cached model therefore shows no button at all: it is simply
              there, which is the point. */}
          {linked && showViewer && !viewerOpen && (
            <button
              onClick={() => setViewerOpen(true)}
              style={smallBtn(false)}
            >{`${VIEW_3D} View 3D — ~3 calls`}</button>
          )}
          {/* Download IFC (ON-7 / ON-13). Offered ONLY when the raw GLB is already on
              disk, because the route refuses otherwise (409) and a button that always
              appears and sometimes errors teaches people to distrust it. `cached.raw` is
              what decides: everything after it is local, so an offer made on `raw` is an
              offer the route can keep.
              ⚠ NO COST IN THE LABEL, deliberately, unlike "View 3D — ~3 calls". This
              action cannot reach Onshape at all (check:ifc-standalone), so quoting a
              number here would be quoting a cost that does not exist. */}
          {/* THE WARNING BELONGS TO THE DOWNLOAD, ONCE. Alex, 2026-08-16: "the first one
              that needs the download should get the warning… if IFC, then download w/o
              warning." So this button is offered on every linked model, and quotes calls
              only when it would actually spend them — the same bargain View 3D makes. It
              passes `budget`, which is what lets the server export for a caller that has
              shown the cost; the Action-URL form passes none and can never spend.
              An earlier version hid the button until View 3D had been clicked, which made
              a one-click action into a two-step and hid the capability where you would
              look for it. */}
          {linked && cached && cached.assembly !== false && (
            <a
              href={`/api/onshape/ifc?ref=${encodeURIComponent(onshapeRef)}${cached.raw ? '' : `&budget=${cached.ifcCalls || 3}`}${downloadName ? `&name=${encodeURIComponent(downloadName)}` : ''}`}
              style={{ ...smallBtn(false), textDecoration: 'none' }}
              title={
                cached.ifc ? 'Already converted — instant'
                  : cached.raw ? 'Converts the model already downloaded — no Onshape call'
                    : 'Downloads the geometry first, then converts it'
              }
            >{`⬇ Download IFC${cached.ifc ? '' : cached.raw ? ' — ~1 min' : ` — ~${cached.ifcCalls || 3} calls`}`}</a>
          )}
          {/* A PART STUDIO gets the reason, not the button. `assembly === false` is a real
              answer, not "not yet": the file has no Instance nodes, so every IFC element
              would be anonymous, and fetching more geometry cannot change that — which is
              why it is worth knowing BEFORE spending three calls to find out. */}
          {linked && cached?.assembly === false && (
            <Text kind="note">
              IFC needs an assembly — this is a part studio
            </Text>
          )}
        </div>

        {linked && showRef && (
          <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)', wordBreak: 'break-all' }}>
            {onshapeRef}
          </div>
        )}

        {thumbError && (
          <Text kind="deprecated" as="div">
            {WARN} {thumbError}
          </Text>
        )}

        {children}

        {viewerOpen && (
          <OnshapeModelViewer
            onshapeRef={onshapeRef}
            height={viewerHeight}
            large={enlarged}
            onToggleLarge={() => setEnlarged(v => !v)}
          />
        )}
      </div>
    </div>
  );

  // title='' suppresses the row entirely (some callers provide their own
  // heading right above this section instead — see each call site).
  const titleRow = title
    ? (
      <SectionTitle
        na={!applicable}
        naReason={naReason}
        collapsed={canCollapse ? collapsed : undefined}
        onToggle={canCollapse ? toggle : undefined}
        // The item's picture beside the 3D MODEL title (Alex, 2026-08-27). It is
        // the one title where the picture stays visible when the section is
        // COLLAPSED — which is the point: closed, the row still says what this
        // is a model OF, instead of only that a model exists.
        icon={dbId ? <ImageCell dbId={dbId} onshapeRef={onshapeRef} size={20} /> : null}
        // COS-1(a): the closed state names the action instead of a bare chevron
        // — "Link" when nothing's linked yet, "Sync" when there's already a ref
        // to re-check against Onshape.
        collapsedLabel={canCollapse ? (linked ? 'Sync' : 'Link') : null}
      >{title}</SectionTitle>
    )
    : null;

  // Not applicable → the title alone says so (`#NA`). No body at all: there is
  // nothing to link and nothing to add, so rendering the editor would invite an
  // action that can never be right.
  // Collapsed → no body either, which is what keeps a closed section from
  // reaching any Onshape call.
  const showBody = applicable && !(canCollapse && collapsed);

  if (!frame) {
    return (
      <div>
        {titleRow}
        {showBody && body}
      </div>
    );
  }
  return (
    <div style={{ ...CARD_STYLE, flexShrink: 0 }}>
      {titleRow}
      {showBody && body}
    </div>
  );
}
